// Settings → Team: the confirmation before removing a person or handing them ownership.
//
// Namespace `team`; owner: the Team settings surface (ui/company/team*.tsx).
// Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
// `{email}` is the person's address; `{name}` is their display name, or the
// address when they have none. The confirm button names the person and the
// act ("Remove Erik Johansson"), never a bare "Confirm" (DESIGN-SPEC §6.10).
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  removeTitle: 'Remove access?',
  removeDescription:
    '{email} will lose organisation access and their sessions will end. Their private work is preserved.',
  removeConfirm: 'Remove {name}',
  transferTitle: 'Transfer ownership?',
  transferDescription:
    '{email} will become Owner. You will become an Admin and will no longer manage access.',
  transferConfirm: 'Make {name} owner',
  revoke: 'Revoke',
  revokeTitle: 'Revoke invitation?',
  revokeDescription:
    'The invitation link for {email} will stop working. You can invite them again at any time.',
  revokeConfirm: 'Revoke invitation',
};

export type TeamCopyKey = keyof typeof en;
type Copy = Record<TeamCopyKey, string>;

const sv: Copy = {
  removeTitle: 'Ta bort åtkomst?',
  removeDescription:
    '{email} förlorar åtkomsten till organisationen och personens sessioner avslutas. Personens privata arbete sparas.',
  removeConfirm: 'Ta bort {name}',
  transferTitle: 'Överlåta ägarskapet?',
  transferDescription:
    '{email} blir ägare. Du blir administratör och hanterar inte längre åtkomsten.',
  transferConfirm: 'Gör {name} till ägare',
  revoke: 'Återkalla',
  revokeTitle: 'Återkalla inbjudan?',
  revokeDescription:
    'Inbjudningslänken till {email} slutar fungera. Du kan bjuda in personen igen när som helst.',
  revokeConfirm: 'Återkalla inbjudan',
};

const da: Copy = {
  removeTitle: 'Fjern adgang?',
  removeDescription:
    '{email} mister adgangen til organisationen, og personens sessioner afsluttes. Personens private arbejde bevares.',
  removeConfirm: 'Fjern {name}',
  transferTitle: 'Overdrag ejerskab?',
  transferDescription:
    '{email} bliver ejer. Du bliver administrator og administrerer ikke længere adgangen.',
  transferConfirm: 'Gør {name} til ejer',
  revoke: 'Tilbagekald',
  revokeTitle: 'Tilbagekald invitationen?',
  revokeDescription:
    'Invitationslinket til {email} holder op med at virke. Du kan invitere personen igen når som helst.',
  revokeConfirm: 'Tilbagekald invitation',
};

const de: Copy = {
  removeTitle: 'Zugriff entfernen?',
  removeDescription:
    '{email} verliert den Zugriff auf die Organisation, und die Sitzungen dieser Person werden beendet. Ihre private Arbeit bleibt erhalten.',
  removeConfirm: '{name} entfernen',
  transferTitle: 'Inhaberschaft übertragen?',
  transferDescription:
    '{email} wird Inhaber. Sie werden Admin und verwalten den Zugriff nicht mehr.',
  transferConfirm: '{name} zum Inhaber machen',
  revoke: 'Widerrufen',
  revokeTitle: 'Einladung widerrufen?',
  revokeDescription:
    'Der Einladungslink für {email} funktioniert dann nicht mehr. Sie können die Person jederzeit erneut einladen.',
  revokeConfirm: 'Einladung widerrufen',
};

const nl: Copy = {
  removeTitle: 'Toegang intrekken?',
  removeDescription:
    '{email} verliest de toegang tot de organisatie en de sessies van deze persoon worden beëindigd. Het privéwerk blijft bewaard.',
  removeConfirm: '{name} verwijderen',
  transferTitle: 'Eigendom overdragen?',
  transferDescription:
    '{email} wordt eigenaar. Je wordt beheerder en beheert de toegang niet langer.',
  transferConfirm: '{name} eigenaar maken',
  revoke: 'Intrekken',
  revokeTitle: 'Uitnodiging intrekken?',
  revokeDescription:
    'De uitnodigingslink voor {email} werkt dan niet meer. Je kunt deze persoon altijd opnieuw uitnodigen.',
  revokeConfirm: 'Uitnodiging intrekken',
};

const fr: Copy = {
  removeTitle: 'Retirer l’accès ?',
  removeDescription:
    '{email} perdra l’accès à l’organisation et ses sessions prendront fin. Son travail privé est conservé.',
  removeConfirm: 'Retirer {name}',
  transferTitle: 'Transférer la propriété ?',
  transferDescription:
    '{email} deviendra propriétaire. Vous deviendrez administrateur et ne gérerez plus les accès.',
  transferConfirm: 'Nommer {name} propriétaire',
  revoke: 'Révoquer',
  revokeTitle: 'Révoquer l’invitation ?',
  revokeDescription:
    'Le lien d’invitation envoyé à {email} ne fonctionnera plus. Vous pourrez l’inviter à nouveau à tout moment.',
  revokeConfirm: 'Révoquer l’invitation',
};

const es: Copy = {
  removeTitle: '¿Quitar el acceso?',
  removeDescription:
    '{email} perderá el acceso a la organización y sus sesiones se cerrarán. Su trabajo privado se conserva.',
  removeConfirm: 'Quitar a {name}',
  transferTitle: '¿Transferir la propiedad?',
  transferDescription:
    '{email} pasará a ser propietario. Usted pasará a ser administrador y dejará de gestionar el acceso.',
  transferConfirm: 'Hacer propietario a {name}',
  revoke: 'Revocar',
  revokeTitle: '¿Revocar la invitación?',
  revokeDescription:
    'El enlace de invitación para {email} dejará de funcionar. Puede volver a invitar a esta persona en cualquier momento.',
  revokeConfirm: 'Revocar invitación',
};

const it: Copy = {
  removeTitle: 'Rimuovere l’accesso?',
  removeDescription:
    '{email} perderà l’accesso all’organizzazione e le sue sessioni verranno chiuse. Il suo lavoro privato viene conservato.',
  removeConfirm: 'Rimuovi {name}',
  transferTitle: 'Trasferire la proprietà?',
  transferDescription:
    '{email} diventerà proprietario. Diventerai amministratore e non gestirai più gli accessi.',
  transferConfirm: 'Rendi {name} proprietario',
  revoke: 'Revoca',
  revokeTitle: 'Revocare l’invito?',
  revokeDescription:
    'Il link di invito per {email} smetterà di funzionare. Potrai invitare di nuovo questa persona in qualsiasi momento.',
  revokeConfirm: 'Revoca invito',
};

const pt: Copy = {
  removeTitle: 'Remover o acesso?',
  removeDescription:
    '{email} perderá o acesso à organização e as respetivas sessões serão terminadas. O trabalho privado é mantido.',
  removeConfirm: 'Remover {name}',
  transferTitle: 'Transferir a propriedade?',
  transferDescription:
    '{email} passará a ser proprietário. Passará a ser administrador e deixará de gerir o acesso.',
  transferConfirm: 'Tornar {name} proprietário',
  revoke: 'Revogar',
  revokeTitle: 'Revogar o convite?',
  revokeDescription:
    'A ligação de convite para {email} deixará de funcionar. Pode voltar a convidar esta pessoa a qualquer momento.',
  revokeConfirm: 'Revogar convite',
};

const fi: Copy = {
  removeTitle: 'Poistetaanko käyttöoikeus?',
  removeDescription:
    '{email} menettää organisaation käyttöoikeuden, ja hänen istuntonsa päättyvät. Hänen yksityinen työnsä säilyy.',
  removeConfirm: 'Poista {name}',
  transferTitle: 'Siirretäänkö omistajuus?',
  transferDescription:
    '{email} tulee omistajaksi. Sinusta tulee ylläpitäjä, etkä enää hallinnoi käyttöoikeuksia.',
  transferConfirm: 'Tee käyttäjästä {name} omistaja',
  revoke: 'Peru',
  revokeTitle: 'Perutaanko kutsu?',
  revokeDescription:
    'Kutsulinkki osoitteeseen {email} lakkaa toimimasta. Voit kutsua henkilön uudelleen milloin tahansa.',
  revokeConfirm: 'Peru kutsu',
};

const et: Copy = {
  removeTitle: 'Kas eemaldada juurdepääs?',
  removeDescription:
    '{email} kaotab juurdepääsu organisatsioonile ja tema seansid lõpetatakse. Tema privaatne töö säilib.',
  removeConfirm: 'Eemalda {name}',
  transferTitle: 'Kas anda omandiõigus üle?',
  transferDescription:
    '{email} saab omanikuks. Sinust saab administraator ja sa ei halda enam juurdepääsu.',
  transferConfirm: 'Tee {name} omanikuks',
  revoke: 'Võta tagasi',
  revokeTitle: 'Kas võtta kutse tagasi?',
  revokeDescription:
    'Kutselink aadressile {email} lakkab töötamast. Saad selle inimese igal ajal uuesti kutsuda.',
  revokeConfirm: 'Võta kutse tagasi',
};

const lv: Copy = {
  removeTitle: 'Vai noņemt piekļuvi?',
  removeDescription:
    '{email} zaudēs piekļuvi organizācijai, un šīs personas sesijas tiks beigtas. Privātais darbs tiek saglabāts.',
  removeConfirm: 'Noņemt {name}',
  transferTitle: 'Vai nodot īpašumtiesības?',
  transferDescription:
    '{email} kļūs par īpašnieku. Jūs kļūsiet par administratoru un vairs nepārvaldīsiet piekļuvi.',
  transferConfirm: 'Padarīt {name} par īpašnieku',
  revoke: 'Atsaukt',
  revokeTitle: 'Vai atsaukt uzaicinājumu?',
  revokeDescription:
    'Uzaicinājuma saite uz {email} vairs nedarbosies. Jūs varat šo personu uzaicināt vēlreiz jebkurā laikā.',
  revokeConfirm: 'Atsaukt uzaicinājumu',
};

const lt: Copy = {
  removeTitle: 'Pašalinti prieigą?',
  removeDescription:
    '{email} neteks prieigos prie organizacijos, o šio asmens sesijos bus baigtos. Privatus darbas išsaugomas.',
  removeConfirm: 'Pašalinti {name}',
  transferTitle: 'Perduoti savininko teises?',
  transferDescription:
    '{email} taps savininku. Jūs tapsite administratoriumi ir nebevaldysite prieigos.',
  transferConfirm: 'Paskirti {name} savininku',
  revoke: 'Panaikinti',
  revokeTitle: 'Panaikinti kvietimą?',
  revokeDescription:
    'Kvietimo nuoroda {email} nebeveiks. Šį asmenį galite pakviesti dar kartą bet kada.',
  revokeConfirm: 'Panaikinti kvietimą',
};

const pl: Copy = {
  removeTitle: 'Usunąć dostęp?',
  removeDescription:
    '{email} straci dostęp do organizacji, a sesje tej osoby zostaną zakończone. Jej prywatna praca zostanie zachowana.',
  removeConfirm: 'Usuń {name}',
  transferTitle: 'Przekazać własność?',
  transferDescription:
    '{email} zostanie właścicielem. Ty zostaniesz administratorem i nie będziesz już zarządzać dostępem.',
  transferConfirm: 'Ustaw {name} jako właściciela',
  revoke: 'Wycofaj',
  revokeTitle: 'Wycofać zaproszenie?',
  revokeDescription:
    'Link z zaproszeniem dla {email} przestanie działać. Możesz ponownie zaprosić tę osobę w dowolnym momencie.',
  revokeConfirm: 'Wycofaj zaproszenie',
};

const cs: Copy = {
  removeTitle: 'Odebrat přístup?',
  removeDescription:
    '{email} ztratí přístup k organizaci a relace této osoby budou ukončeny. Její soukromá práce zůstane zachována.',
  removeConfirm: 'Odebrat {name}',
  transferTitle: 'Převést vlastnictví?',
  transferDescription:
    '{email} se stane vlastníkem. Vy se stanete správcem a přestanete spravovat přístup.',
  transferConfirm: 'Nastavit {name} jako vlastníka',
  revoke: 'Odvolat',
  revokeTitle: 'Odvolat pozvánku?',
  revokeDescription:
    'Odkaz pozvánky pro {email} přestane fungovat. Tuto osobu můžete kdykoli pozvat znovu.',
  revokeConfirm: 'Odvolat pozvánku',
};

const sk: Copy = {
  removeTitle: 'Odobrať prístup?',
  removeDescription:
    '{email} stratí prístup k organizácii a relácie tejto osoby sa ukončia. Jej súkromná práca zostane zachovaná.',
  removeConfirm: 'Odobrať {name}',
  transferTitle: 'Previesť vlastníctvo?',
  transferDescription:
    '{email} sa stane vlastníkom. Vy sa stanete správcom a prestanete spravovať prístup.',
  transferConfirm: 'Nastaviť {name} ako vlastníka',
  revoke: 'Odvolať',
  revokeTitle: 'Odvolať pozvánku?',
  revokeDescription:
    'Odkaz pozvánky pre {email} prestane fungovať. Túto osobu môžete kedykoľvek pozvať znova.',
  revokeConfirm: 'Odvolať pozvánku',
};

const sl: Copy = {
  removeTitle: 'Želite odstraniti dostop?',
  removeDescription:
    '{email} bo izgubil dostop do organizacije, seje te osebe pa se bodo končale. Njeno zasebno delo se ohrani.',
  removeConfirm: 'Odstrani {name}',
  transferTitle: 'Želite prenesti lastništvo?',
  transferDescription:
    '{email} bo postal lastnik. Vi boste postali skrbnik in ne boste več upravljali dostopa.',
  transferConfirm: 'Nastavi {name} za lastnika',
  revoke: 'Umakni',
  revokeTitle: 'Želite umakniti povabilo?',
  revokeDescription:
    'Povezava povabila za {email} ne bo več delovala. To osebo lahko kadar koli znova povabite.',
  revokeConfirm: 'Umakni povabilo',
};

const hr: Copy = {
  removeTitle: 'Ukloniti pristup?',
  removeDescription:
    '{email} izgubit će pristup organizaciji, a sesije te osobe bit će prekinute. Njezin privatni rad ostaje sačuvan.',
  removeConfirm: 'Ukloni {name}',
  transferTitle: 'Prenijeti vlasništvo?',
  transferDescription:
    '{email} postat će vlasnik. Vi ćete postati administrator i više nećete upravljati pristupom.',
  transferConfirm: 'Postavi {name} za vlasnika',
  revoke: 'Opozovi',
  revokeTitle: 'Opozvati pozivnicu?',
  revokeDescription:
    'Poveznica pozivnice za {email} prestat će raditi. Tu osobu možete ponovno pozvati u bilo kojem trenutku.',
  revokeConfirm: 'Opozovi pozivnicu',
};

const bg: Copy = {
  removeTitle: 'Премахване на достъпа?',
  removeDescription:
    '{email} ще загуби достъп до организацията и сесиите на този човек ще бъдат прекратени. Личната му работа се запазва.',
  removeConfirm: 'Премахни {name}',
  transferTitle: 'Прехвърляне на собствеността?',
  transferDescription:
    '{email} ще стане собственик. Вие ще станете администратор и вече няма да управлявате достъпа.',
  transferConfirm: 'Направи {name} собственик',
  revoke: 'Оттегли',
  revokeTitle: 'Оттегляне на поканата?',
  revokeDescription:
    'Връзката за покана до {email} ще спре да работи. Можете да поканите този човек отново по всяко време.',
  revokeConfirm: 'Оттегли поканата',
};

const el: Copy = {
  removeTitle: 'Κατάργηση πρόσβασης;',
  removeDescription:
    'Ο χρήστης {email} θα χάσει την πρόσβαση στον οργανισμό και οι συνεδρίες του θα λήξουν. Η ιδιωτική του εργασία διατηρείται.',
  removeConfirm: 'Αφαίρεση {name}',
  transferTitle: 'Μεταβίβαση κυριότητας;',
  transferDescription:
    'Ο χρήστης {email} θα γίνει κάτοχος. Εσείς θα γίνετε διαχειριστής και δεν θα διαχειρίζεστε πλέον την πρόσβαση.',
  transferConfirm: 'Ορισμός {name} ως κατόχου',
  revoke: 'Ανάκληση',
  revokeTitle: 'Ανάκληση πρόσκλησης;',
  revokeDescription:
    'Ο σύνδεσμος πρόσκλησης για {email} θα πάψει να λειτουργεί. Μπορείτε να προσκαλέσετε ξανά αυτό το άτομο οποιαδήποτε στιγμή.',
  revokeConfirm: 'Ανάκληση πρόσκλησης',
};

const hu: Copy = {
  removeTitle: 'Eltávolítja a hozzáférést?',
  removeDescription:
    '{email} elveszíti a hozzáférését a szervezethez, és a munkamenetei véget érnek. A privát munkája megmarad.',
  removeConfirm: '{name} eltávolítása',
  transferTitle: 'Átruházza a tulajdonjogot?',
  transferDescription:
    '{email} lesz a tulajdonos. Ön adminisztrátor lesz, és többé nem kezeli a hozzáféréseket.',
  transferConfirm: '{name} legyen a tulajdonos',
  revoke: 'Visszavonás',
  revokeTitle: 'Visszavonja a meghívót?',
  revokeDescription:
    'A meghívólink ({email}) többé nem fog működni. Ezt a személyt bármikor újra meghívhatja.',
  revokeConfirm: 'Meghívó visszavonása',
};

const ro: Copy = {
  removeTitle: 'Eliminați accesul?',
  removeDescription:
    '{email} va pierde accesul la organizație, iar sesiunile acestei persoane se vor încheia. Munca sa privată este păstrată.',
  removeConfirm: 'Eliminați {name}',
  transferTitle: 'Transferați proprietatea?',
  transferDescription:
    '{email} va deveni proprietar. Veți deveni administrator și nu veți mai gestiona accesul.',
  transferConfirm: 'Faceți-l pe {name} proprietar',
  revoke: 'Revocă',
  revokeTitle: 'Revocați invitația?',
  revokeDescription:
    'Linkul de invitație pentru {email} nu va mai funcționa. Puteți invita din nou această persoană oricând.',
  revokeConfirm: 'Revocă invitația',
};

const ga: Copy = {
  removeTitle: 'An bhfuil fonn ort an rochtain a bhaint?',
  removeDescription:
    'Caillfidh {email} rochtain ar an eagraíocht agus cuirfear deireadh leis na seisiúin. Coinneofar an obair phríobháideach.',
  removeConfirm: 'Bain {name}',
  transferTitle: 'An bhfuil fonn ort an úinéireacht a aistriú?',
  transferDescription:
    'Beidh {email} ina úinéir. Beidh tusa i do riarthóir agus ní bheidh tú ag bainistiú rochtana a thuilleadh.',
  transferConfirm: 'Déan úinéir de {name}',
  revoke: 'Aisghair',
  revokeTitle: 'An bhfuil fonn ort an cuireadh a aisghairm?',
  revokeDescription:
    'Stopfaidh an nasc cuiridh do {email} ag obair. Is féidir leat cuireadh a thabhairt don duine seo arís am ar bith.',
  revokeConfirm: 'Aisghair an cuireadh',
};

const mt: Copy = {
  removeTitle: 'Tneħħi l-aċċess?',
  removeDescription:
    '{email} se jitlef l-aċċess għall-organizzazzjoni u s-sessjonijiet tiegħu jintemmu. Ix-xogħol privat tiegħu jinżamm.',
  removeConfirm: 'Neħħi lil {name}',
  transferTitle: 'Tittrasferixxi s-sjieda?',
  transferDescription:
    '{email} se jsir sid. Int se ssir amministratur u ma tibqax timmaniġġja l-aċċess.',
  transferConfirm: 'Agħmel lil {name} sid',
  revoke: 'Irtira',
  revokeTitle: 'Tirtira l-istedina?',
  revokeDescription:
    'Il-link tal-istedina għal {email} se jieqaf jaħdem. Tista’ terġa’ tistieden lil din il-persuna meta trid.',
  revokeConfirm: 'Irtira l-istedina',
};

const teamCopy: NamespaceCatalog<TeamCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default teamCopy;
